import { Component, Input, Output, EventEmitter, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../../../core/services/settings.service';
import { firstValueFrom, debounceTime, distinctUntilChanged } from 'rxjs';
import { toast } from 'ngx-sonner';

import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { SharedModule } from 'primeng/api';
import { AppButton } from '../../../../../../shared/components/app-button/app-button';

@Component({
  selector: 'app-dtp-link-treatment-modal',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    TranslateModule,
    Dialog,
    SharedModule,
    InputText,
    IconField,
    InputIcon,
    AppButton
  ],
  templateUrl: './dtp-link-treatment-modal.html',
})
export class DtpLinkTreatmentModal implements OnInit {
  @Input() diagnosticId!: number;
  @Input() branchId!: number;
  @Input() excludedTreatmentIds: number[] = [];
  
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  availableTreatments: any[] = [];
  selectedTreatments: any[] = [];
  allTreatments: any[] = [];
  loading = false;
  saving = false;
  searchControl = new FormControl('');

  async ngOnInit() {
    await this.loadAvailableTreatments();
    
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.filterTreatments();
    });
  }

  async loadAvailableTreatments() {
    this.loading = true;
    try {
      const data = await firstValueFrom(this.settingsService.getAvailableTreatments(this.branchId));
      this.allTreatments = data || [];
      this.filterTreatments();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  filterTreatments() {
    const search = (this.searchControl.value || '').toLowerCase();
    this.availableTreatments = this.allTreatments.filter(t => 
      !this.excludedTreatmentIds.includes(t.id) &&
      !this.selectedTreatments.some(st => st.id === t.id) &&
      (t.name?.toLowerCase().includes(search) || t.description?.toLowerCase().includes(search))
    );
    this.cdr.detectChanges();
  }

  toggleTreatment(treatment: any) {
    this.selectedTreatments.push(treatment);
    this.filterTreatments();
  }

  removeSelected(treatment: any) {
    this.selectedTreatments = this.selectedTreatments.filter(t => t.id !== treatment.id);
    this.filterTreatments();
  }

  async save() {
    if (this.selectedTreatments.length === 0) return;
    this.saving = true;
    try {
      const treatmentIds = this.selectedTreatments.map(t => t.id);
      await firstValueFrom(this.settingsService.linkTreatmentsToDiagnostic(this.diagnosticId, treatmentIds, this.branchId));
      toast.success(this.translate.instant('save_success'));
      this.saved.emit();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_saving_data'));
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }
}
